import React, { useState } from "react";
import { ExternalLink, LogOut } from "lucide-react";
import { useAuth } from "@talis/auth-js";
import { Tile, PreviewBanner } from "@/components/common/Tile";
import { Loading } from "@/components/common/Loading";
import { useMe } from "@/hooks/useMe";

type NotificationMode = "in_app" | "email" | "sms";

const NOTIFICATION_OPTIONS: { value: NotificationMode; label: string }[] = [
  { value: "in_app", label: "In App" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

const SECTIONS = [
  { id: "notifs", label: "Notification Settings" },
  { id: "security", label: "Account Security" },
];

export default function AccountSettings() {
  const { data: me, isLoading } = useMe();
  const { logout } = useAuth();
  const [notificationMode, setNotificationMode] =
    useState<NotificationMode>("in_app");

  if (isLoading) return <Loading />;

  function signOut() {
    // Package logout: cross-tab broadcast + /sso/logout (federated).
    logout();
  }

  return (
    <div className="flex flex-col gap-md p-md">
      <PreviewBanner>
        Notification preferences pending backend support.
      </PreviewBanner>

      <div className="grid grid-cols-12 gap-md">
        {/* Sticky section nav */}
        <aside className="col-span-12 md:col-span-3">
          <div className="md:sticky md:top-md">
            <Tile>
              <nav className="flex flex-col">
                {SECTIONS.map(s => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="px-sm py-xs rounded-sm text-sm text-text hover:bg-bg"
                  >
                    {s.label}
                  </a>
                ))}
              </nav>
              {me?.email && (
                <div className="mt-md pt-sm border-t border-border text-xs text-textLight">
                  Signed in as
                  <div className="text-text truncate">{me.email}</div>
                </div>
              )}
            </Tile>
          </div>
        </aside>

        <div className="col-span-12 md:col-span-9 flex flex-col gap-md">
          <Tile title={<span id="notifs">Notification Settings</span>}>
            <div className="flex flex-col gap-sm max-w-sm">
              <label className="text-xs text-textLight">
                Notification Preference
              </label>
              <select
                value={notificationMode}
                onChange={e =>
                  setNotificationMode(e.target.value as NotificationMode)
                }
                className="rounded-md border border-border bg-surface px-sm py-xs text-sm text-text"
              >
                {NOTIFICATION_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled
                className="self-start rounded-md border border-info-text/40 bg-info-bg text-info-text px-sm py-xs text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Notification Settings
              </button>
            </div>
          </Tile>

          <Tile title={<span id="security">Account Security</span>}>
            <div className="flex flex-col gap-md">
              <p className="text-sm text-textLight">
                Your account is managed by ait-auth via OIDC. Password changes,
                MFA, and identity settings live there.
              </p>

              <a
                href="https://auth.talis-ai.com/account"
                target="_blank"
                rel="noreferrer"
                className="self-start inline-flex items-center gap-xs rounded-md border border-info-text/40 bg-info-bg text-info-text px-sm py-xs text-sm hover:opacity-80"
              >
                <ExternalLink size={14} />
                Manage account at auth.talis-ai.com
              </a>

              <div className="pt-sm border-t border-border">
                <button
                  type="button"
                  onClick={signOut}
                  className="inline-flex items-center gap-xs rounded-md border border-danger-text/40 bg-danger-bg text-danger-text px-sm py-xs text-sm hover:opacity-80"
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            </div>
          </Tile>
        </div>
      </div>
    </div>
  );
}
